import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { Chart, registerables } from "chart.js";
import { OverlayTrigger, Popover } from 'react-bootstrap';
import axios from "axios";
import Csvreader from "./Csvreader";
import DampingRatioCell from "./DampingRatioCell";
import FrfComparisonStatus from "./FrfComparisonStatus";
import {
  BASE_LINE_LABEL,
  COMPARISON_LINE_LABEL,
  PEAK_MATCH_TOLERANCE_HZ,
  findBaselineForMachineModel,
  findNearestPeakIndex,
  hasComparableDifference,
  isAmplitudeDifferenceAbnormal,
  isFrequencyDifferenceAbnormal
} from './frfBaseline';
import zoomPlugin from 'chartjs-plugin-zoom';
import { API_BASE_URL } from './config';
import "./style.css";
import "./FrequencyResponseFunction.css";

import 'bootstrap/dist/css/bootstrap.min.css';

Chart.register(...registerables, zoomPlugin);

const FRFZ = () => {
  const navigate = useNavigate();
  const onMachineManagement = (e) => {
    e.preventDefault();
    navigate('/machine-management');
  };
  const onMachineCreate = (e) => {
    e.preventDefault();
    navigate('/machine-create');
  };

  const { axis } = useParams();
  const [activeTab, setActiveTab] = useState(axis || "Z");
  useEffect(() => {
    setActiveTab(axis || "Z");
  }, [axis]);

  //從Csvreader抓取loading狀態，若為true，禁用軸向切換，出現提示字
  const [loading, setLoading] = useState(localStorage.getItem("loading") === "true");
  //每0.5秒更新一次狀態
  useEffect(() => {
    const interval = setInterval(() => {
      const currentLoading = localStorage.getItem("loading") === "true";
      setLoading(currentLoading);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [csvData, setcsvData] = useState({
    Amp_peak_freq_data: Array(6).fill(''),
    Amp_peak_data: Array(6).fill(''),
    Damping_ratio_data: Array(6).fill(null),
    Error_freq: Array(6).fill(''),
    Error_amp: Array(6).fill(''),
    ch_name: "",
    filename: "",
    freq: [],
    magdb: [],
    magn: [],
    phase: [],
    time: ""
  });

  const [updataZ, setupDataZ] = useState([]);
  const [newUpdataZ, setUpdataZ] = useState([]);
  const [coverData, setcoverData] = useState([]);
  const [lineLabels, setLineLabels] = useState([]);

  const onMachineManagementSave = async (e) => {
    e.preventDefault();

    // 取用 csvDataX 和 csvDataY
    const savedCsvDataX = localStorage.getItem("csvDataX");
    const savedCsvDataY = localStorage.getItem("csvDataY");

    
    // 如果檔案或資料未正確匯入，則儲存失敗
    if (!csvData.filename || !savedCsvDataX || !savedCsvDataY) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      return;
    }
    

    // 取用 updataX, updataY 和 updataZ
    const savedUpdataX = localStorage.getItem("updataX");
    const savedUpdataY = localStorage.getItem("updataY");
    const savedUpdataZ = localStorage.getItem("updataZ");

    // 淺拷貝 formData 並命名為 combinedData
    const combinedData = { 
        ...(localStorage.getItem("KeepData") ? JSON.parse(localStorage.getItem("KeepData")) : {}), 
        username: localStorage.getItem("username") 
    };

    // 直接將解析後的資料儲存到 combinedData 中
    combinedData.updataX = savedUpdataX ? JSON.parse(savedUpdataX) : null;
    combinedData.updataY = savedUpdataY ? JSON.parse(savedUpdataY) : null;
    combinedData.updataZ = savedUpdataZ ? JSON.parse(savedUpdataZ) : updataZ;

    // 邏輯判斷，檢查資料是否有效
    const isDataInvalid = (data) => {
      const chartData = data?.chartData;
      return (
        !chartData ||
        !Array.isArray(chartData.freq) ||
        chartData.freq.length === 0 ||
        !Array.isArray(chartData.magn) ||
        chartData.magn.length === 0
      );
    };
    
    // 如果有任何資料不完整，則顯示錯誤訊息
    if (isDataInvalid(combinedData.updataX) || isDataInvalid(combinedData.updataY) || isDataInvalid(combinedData.updataZ)) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      return;
    }

    try {
      // 發送 POST 請求儲存資料
      const response = await axios.post(`${API_BASE_URL}/upload/formData`, combinedData);
      console.log('儲存成功', response.data);
      // 導向到機台管理頁面
      navigate('/machine-management');
    } catch (error) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入', error);
    }
  };

  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineList, setMachineList] = useState([]);

  const handleConfirmSelection = () => {
    const newChartData = selectedMachines.map((machineId) => {
      const machine = machineList.find((m) => m._id === machineId);
      if (!machine) return null;
      const r = Math.floor(Math.random() * 255);
      const g = Math.floor(Math.random() * 255);
      const b = Math.floor(Math.random() * 255);
      return {
        label: machine.updataZ.chartData.ch_name,
        borderColor: `rgb(${r}, ${g}, ${b})`,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
        data: Array.isArray(machine.updataZ.chartData.magn) ? machine.updataZ.chartData.magn : [machine.updataZ.chartData.magn],
        fill: true,
        tension: 0.4,
        labels: machine.updataZ.chartData.freq,
      };
    }).filter(item => item !== null);
    setcoverData(newChartData);
    setLineLabels(newChartData[0]?.labels.slice(9) || [])
  };

  useEffect(() => {
    if (selectedMachines.length > 0) {
      handleConfirmSelection();
    }
  }, [activeTab]);

  const lineChartRef = useRef(null);

  const createLineChart = (chartRef, datasets) => {
    const chartInstance = new Chart(lineChartRef.current, {
      type: "line",
      data: {
        labels: chartRef,
        datasets: datasets
      },
      options: {
        layout: {
          padding: { right: -60 } // 圖例位置調整
        },
        scales: {
          y: {
            ticks: { beginAtZero: true },
            title: { display: true, text: 'Amplitude (μm/n)', font: { size: 14 }, padding: 15 }
          },
          x: {
            type: 'linear',
            min: 20, max: 200,
            title: { display: true, text: 'Frequency (Hz)', font: { size: 14 }, padding: 15 },
            ticks: {
              beginAtZero: true,
              stepSize: 5,
              callback: function (value) {
                return Math.round(value);
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        fill: true,
        elements: {
          point: { radius: 0, hitRadius: 10 },
          line: { tension: 0.4 }
        },
        plugins: {
          legend: {
            position: 'bottom',
            display: true,
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              font: { size: 13 } // 折線圖圖例尺寸
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || '';
                const xValue = context.label;
                const yValue = context.raw.toFixed(4);
                return `${label}   ( ${xValue} , ${yValue} )`;
              },
              title: function () {
                return '';
              }
            }
          },
          datalabels: { display: false },
          zoom: {
            pan: {
              enabled: false, // 禁用拖曳平移，避免拖動時圖表一起動
            },
            zoom: {
              wheel: { enabled: false }, // 禁用滾輪縮放
              pinch: { enabled: false }, // 禁用觸控縮放
              mode: 'x',
              drag: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                threshold: 2,
                delay: 50 // 增加延遲，讓放大不會即時觸發
              }
            },
            onZoom: ({ chart }) => {
              let xScale = chart.scales.x;
              let min = Math.max(xScale.min, 20);
              let max = Math.min(xScale.max, 200);
              chart.options.scales.x.min = min;
              chart.options.scales.x.max = max;
              chart.options.scales.x.ticks.stepSize = 5;  // 重新設定間距
              chart.update();
            }
          }
        }
      }
    });
    lineChartRef.current.chartInstance = chartInstance;
    return chartInstance;
  };

  function getRandomColor() {
    const r = Math.floor(Math.random() * 255);
    const g = Math.floor(Math.random() * 255);
    const b = Math.floor(Math.random() * 255);
    return {
      borderColor: `rgb(${r}, ${g}, ${b})`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`
    };
  }

  const resetZoom = () => {
    if (lineChartRef.current && lineChartRef.current.chartInstance) {
      lineChartRef.current.chartInstance.resetZoom();
    }
  };

  useEffect(() => {
    let lineChart;
    if (lineChartRef.current)
      lineChart?.destroy();
    lineChart = createLineChart(lineLabels, coverData);
    return () => {
      lineChart?.destroy();
    };
  }, [coverData]);

  const location = useLocation();
  const [formData, setFormData] = useState(location.state?.formData || {});
  const [freqDifferences, setFreqDifferences] = useState([]);
  const [ampDifferences, setAmpDifferences] = useState([]);
  const [baselineData, setBaselineData] = useState([]);

  useEffect(() => {
    console.log('接收到的基本資料', formData);
  }, [formData]);

  useEffect(() => {
    const fetchCastingData = async () => {
      const username = localStorage.getItem('username') || '未知使用者';
      try {
        const response = await axios.get(`${API_BASE_URL}/getdata/formData`, {
          headers: { 'x-username': username }
        });
        const fetchedData = response.data;
        const matchingEntry = findBaselineForMachineModel(fetchedData, formData?.machineModel);
        if (matchingEntry) {
          const freqDiffs = [];
          const ampDiffs = [];

          csvData.Amp_peak_freq_data.forEach((freq, i) => {
            //匯入值
            const amp = csvData.Amp_peak_data[i];
            const matchIndex = findNearestPeakIndex(
              freq,
              matchingEntry.updataZ.Amp_peak_freq_data,
              PEAK_MATCH_TOLERANCE_HZ
            );

            if (matchIndex !== -1) {
              const baseFreq = matchingEntry.updataZ.Amp_peak_freq_data[matchIndex];
              const baseAmp = matchingEntry.updataZ.Amp_peak_data[matchIndex];

              const freqDiff = Math.abs((freq - baseFreq) / baseFreq);
              const ampDiff = Math.abs((amp - baseAmp) / baseAmp);

              /*console.log(`Freq 比較：第 ${i} 筆資料 (${freq}) 與 基準第 ${matchIndex} 筆 (${baseFreq})`);
              console.log(`Amp 比較：第 ${i} 筆資料 (${amp}) 與 基準第 ${matchIndex} 筆 (${baseAmp})，頻率相符於 ${baseFreq}`);*/

              freqDiffs.push((freqDiff * 100).toFixed(1));
              ampDiffs.push((ampDiff * 100).toFixed(1));
            } else {
              /*console.log(`Freq 比較：第 ${i} 筆資料 (${freq}) 無符合的基準值`);
              console.log(`Amp 比較：第 ${i} 筆資料 (${amp}) 無符合頻率的基準值`);*/

              freqDiffs.push(null);
              ampDiffs.push(null);
            }
          });
          setFreqDifferences(freqDiffs);
          setAmpDifferences(ampDiffs);

          // 保留原始峰值；比對差異另外存，避免無基準時把資料洗成空陣列。
          const newUpdataZ = {
            ...updataZ,
            Amp_peak_freq_data: csvData.Amp_peak_freq_data || [],
            Amp_peak_data: csvData.Amp_peak_data || [],
            Damping_ratio_data: csvData.Damping_ratio_data || [],
            Amp_peak_freq_diff: freqDiffs,
            Amp_peak_amp_diff: ampDiffs,
            Amp_peak_freq_size: (csvData.Amp_peak_freq_data || []).length,
            Amp_peak_size: (csvData.Amp_peak_data || []).length
          };

          setUpdataZ(newUpdataZ);
          localStorage.setItem("updataZ", JSON.stringify(newUpdataZ));
        } else {
          setFreqDifferences([]);
          setAmpDifferences([]);
        }

        setBaselineData(fetchedData);
      } catch (error) {
        console.error("Error fetching casting data:", error);
      }
    };
    fetchCastingData();
  }, [formData, csvData]);

  useEffect(() => {
    const stateFormData = location.state?.formData;
    if (stateFormData) {
      localStorage.setItem("formData", JSON.stringify(stateFormData));
      setFormData(stateFormData);
    } else {
      const savedFormData = localStorage.getItem("formData");
      if (savedFormData) {
        setFormData(JSON.parse(savedFormData));
      }
    }
    const savedCsvData = localStorage.getItem("csvDataZ");
    if (savedCsvData) {
      setcsvData(JSON.parse(savedCsvData));
    }
    const savedCoverData = localStorage.getItem("coverDataZ");
    if (savedCoverData) {
      setcoverData(JSON.parse(savedCoverData));
    }
    const savedLineLabels = localStorage.getItem("lineLabelsZ");
    if (savedLineLabels) {
      setLineLabels(JSON.parse(savedLineLabels));
    }
    const savedUpdataZ = localStorage.getItem("updataZ");
    if (savedUpdataZ) {
      setupDataZ(JSON.parse(savedUpdataZ));
    }
  }, [location.state]);

  const handleFileload = (e) => {
    const file = e.target.files[0];
    if (file) {
      Csvreader(file, (data) => {
        console.log('Csvreader data:', data);
        const newCsvData = {
          Amp_peak_freq_data: data.Amp_peak_freq_data || Array(6).fill(''),
          Amp_peak_data: data.Amp_peak_data || Array(6).fill(''),
          Damping_ratio_data: data.Damping_ratio_data || Array(6).fill(null),
          Error_freq: data.Error_freq || Array(6).fill(''),
          Error_amp: data.Error_amp || Array(6).fill(''),
          ch_name: data.chartData?.ch_name || '',
          filename: data.chartData?.filename || '',
          freq: data.chartData?.freq || [],
          magdb: data.chartData?.magdb || [],
          magn: data.chartData?.magn || [],
          phase: data.chartData?.phase || [],
          time: data.chartData?.time || []
        };
        setcsvData(newCsvData);
        localStorage.setItem("csvDataZ", JSON.stringify(newCsvData));
        if (data.chartData && data.chartData.freq) {
          console.log('Valid chartData:', data.chartData);
          setupDataZ(data);
          localStorage.setItem("updataZ", JSON.stringify(data));
          const matchingEntry = findBaselineForMachineModel(baselineData, formData?.machineModel);
          if (!matchingEntry) {
            setFreqDifferences([]);
            setAmpDifferences([]);
          }
          if (matchingEntry) {
            const newCoverData = [
              {
                label: `${matchingEntry.updataZ.chartData.ch_name} (基準值)`,
                data: matchingEntry.updataZ.chartData.magn.slice(9),
                borderColor: getRandomColor().borderColor,
                backgroundColor: getRandomColor().backgroundColor
              },
              {
                label: data.chartData.ch_name,
                data: data.chartData.magn.slice(9),
                borderColor: getRandomColor().borderColor,
                backgroundColor: getRandomColor().backgroundColor
              }
            ];
            // The chart legend describes each upload's role for this machine model.
            newCoverData[0].label = BASE_LINE_LABEL;
            if (matchingEntry) {
              newCoverData[1].label = COMPARISON_LINE_LABEL;
            }
            setcoverData(newCoverData);
            localStorage.setItem("coverDataZ", JSON.stringify(newCoverData));

          } else {
            const newCoverData = [{
              label: data.chartData.ch_name,
              data: data.chartData.magn.slice(9),
              borderColor: getRandomColor().borderColor,
              backgroundColor: getRandomColor().backgroundColor
            }];
            newCoverData[0].label = BASE_LINE_LABEL;
            setcoverData(newCoverData);
            localStorage.setItem("coverDataZ", JSON.stringify(newCoverData));
            setBaselineData([{
              machineModel: formData.machineModel,
              updataZ: data
            }]);
          }
          const newLineLabels = data.chartData.freq.slice(9);
          setLineLabels(newLineLabels);
          localStorage.setItem("lineLabelsZ", JSON.stringify(newLineLabels));
        } else {
          console.error('Invalid chartData:', data);
        }
      });
    }
  };

  //沒值 顯示 '計算中…'，5秒後沒值 顯示 '頻率偏差過大，請匯入其他筆資料再試一次'
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  useEffect(() => {
    //查看每個 index，如果 index 對應的 freqDifferences 和 ampDifferences 都不是 null，就是有效的
    const isEmpty = ((freqDifferences?.length > 0 ? freqDifferences : csvData.Amp_peak_freq_data) || [])
      .map((_, index) => index)
      .filter(index => hasComparableDifference(freqDifferences?.[index]) && hasComparableDifference(ampDifferences?.[index]))
      //沒值
      .length === 0;

    let timer;

    if (csvData.filename && isEmpty) {
      setShowErrorMsg(false); // 先顯示'計算中...'
      timer = setTimeout(() => {
        setShowErrorMsg(true); // 5秒後沒值，顯示'頻率偏差過大...'
      }, 5000);
    } else {
      setShowErrorMsg(false); // 有值就不會有這個訊息
    }
    return () => clearTimeout(timer);
  }, [csvData.filename, freqDifferences, ampDifferences]);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        數值不在標準範圍內，為危險值！
      </Popover.Body>
    </Popover>
  );
  const isComparingWithBaseline = freqDifferences.length > 0 || ampDifferences.length > 0;

  return (
    <div className="d-flex" style={{ width: '81.7%', margin: '0 auto' }}>
      <div className="container-fluid">
        <div style={{ height: '35px', width: '100%' }}></div>

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#" onClick={onMachineManagement} style={{ color: '#B0B7C3', fontWeight: 'bold', fontSize: '15px' }}>機台資料庫</a>
            </li>
            <li
              className="breadcrumb-item"
              aria-current="page"
              style={{ color: '#5B82B1', fontWeight: 'bold', fontSize: '15px' }}>新增頻率響應函數
            </li>
          </ol>
        </nav>

        <div className="frf-action-row">
          <button
            className="reset-btn btn-margin-sm bi bi-arrow-clockwise"
            onClick={resetZoom}
          >
          </button>
          <label
            htmlFor="fileUpload"
            className="btn1 btn-margin-sm"
            style={{
              height: '35px',
              width: '75px',
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: '35px',
              backgroundColor: '#007bff',
              color: '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            上傳檔案
          </label>
          <input
            type="file"
            id="fileUpload"
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleFileload}
          />
        </div>

        <nav>
          <div className="nav nav-tabs" role="tablist">
            {["X", "Y", "Z"].map((axis) => (
              <Link
                key={axis}
                to={`/frequency-response-function/${axis.toLowerCase()}`}
                onClick={(e) => {
                  if (loading) e.preventDefault(); // loading狀態下禁止跳轉
                }}
                className={`nav-link ${activeTab === axis ? "active" : ""} ${loading ? "disabled-link" : ""}`}
              >
                {axis}軸向
              </Link>
            ))}
          </div>
        </nav>

        {loading && <div className="loading-center">匯入中，請稍候...</div>}

        <FrfComparisonStatus
          frequencyDifferences={freqDifferences}
          amplitudeDifferences={ampDifferences}
        />

        <div className="tab-content" id="nav-tabContent">
          <div className="tab-pane show active p-0 my-4">

            <div style={{ width: '99%', height: '500px' }}>
              <canvas ref={lineChartRef}></canvas>
            </div>

            <div className="p-4">
              <div className="table-responsive">
                <table className="table table-striped table-bordered text-center frf-peak-table">
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                  </colgroup>
                  <thead className="custom-thead ">
                    <tr>
                      <th>資料ID</th>
                      <th>頻率</th>
                      <th>振幅</th>
                      <th>阻尼比</th>
                      <th>線差(f)</th>
                      <th>幅差(a)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.filename ? (() => {
                      const rowIndexes = (csvData.Amp_peak_freq_data || [])
                        .map((_, index) => index)
                        .filter(index => {
                          const hasPeak = csvData.Amp_peak_freq_data?.[index] !== null &&
                            csvData.Amp_peak_freq_data?.[index] !== undefined;

                          if (!hasPeak) return false;
                          if (!isComparingWithBaseline) return true;

                          return hasComparableDifference(freqDifferences?.[index]) &&
                            hasComparableDifference(ampDifferences?.[index]);
                        });

                      if (rowIndexes.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>
                              {showErrorMsg
                                ? '峰值比對尚無可對應資料，仍可儲存'
                                : '峰值比對計算中...'}
                            </td>
                          </tr>
                        );
                      }

                      return rowIndexes.map((index, displayIndex) => {
                        const freq = csvData.Amp_peak_freq_data?.[index] || 'NaN';
                        const amp = freq === 'NaN' ? 'NaN' : (csvData.Amp_peak_data?.[index] || 'NaN');
                        const freqDiff = freqDifferences?.[index];
                        const ampDiff = ampDifferences?.[index];
                        const hasFreqDiff = hasComparableDifference(freqDiff);
                        const hasAmpDiff = hasComparableDifference(ampDiff);
                        const hasComparison = hasFreqDiff && hasAmpDiff;

                        return (
                          <tr key={index}>
                            <td>{displayIndex + 1}</td>
                            <td style={{ color: freq === 'NaN' ? '#CCCCCC' : '' }}>{freq}</td>
                            <td style={{ color: amp === 'NaN' ? '#CCCCCC' : '' }}>{amp}</td>
                            <DampingRatioCell value={csvData.Damping_ratio_data?.[index]} />

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={hasComparison && isFrequencyDifferenceAbnormal(freqDiff) ? popover : <></>}
                            >
                              <td
                                style={{
                                  color: hasComparison && isFrequencyDifferenceAbnormal(freqDiff) ? '#EC4643' : (!hasComparison ? '#999' : '')
                                }}
                              >
                                {hasComparison ? `${freqDiff}%` : '-'}
                              </td>
                            </OverlayTrigger>

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={
                                (() => {
                                  if (hasComparison && isAmplitudeDifferenceAbnormal(ampDiff)) {
                                    return popover;
                                  }
                                  return <></>;
                                })()
                              }
                            >
                              <td
                                style={{
                                  color: (() => {
                                    if (hasComparison && isAmplitudeDifferenceAbnormal(ampDiff)) {
                                      return '#EC4643';
                                    }
                                    return !hasComparison ? '#999' : '';
                                  })()
                                }}
                              >
                                {hasComparison ? `${ampDiff}%` : '-'}
                              </td>
                            </OverlayTrigger>
                          </tr>
                        );
                      });
                    })() : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>
                          尚未上傳檔案
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <div className="d-flex justify-content-end" style={{ paddingRight: '10px' }}>
            <button
              className="btn-cancel"
              style={{ height: '35px', width: '75px' }}
              onClick={onMachineCreate}
            >
              上一步
            </button>
          </div>
          <div className="d-flex justify-content-end" style={{ paddingRight: '20px' }}>
            <button
              className="btn-confirm btn-primary"
              style={{ height: '35px', width: '75px' }}
              onClick={onMachineManagementSave}
            >
              儲存資料
            </button>
          </div>
        </div>
        <div style={{ height: '40px', width: '100%' }}></div>
      </div>
    </div >
  );
};

export default FRFZ;
